<!-- App.vue -->
<template>
  <v-app>
    <v-main>
      <router-view />
    </v-main>
    <!-- The simulated push banner (#106) lives at the app level so a salon's
         confirmation surfaces wherever the customer happens to be — the
         storefront, the wizard, the success screen. -->
    <PseudoNotification />
  </v-app>
</template>

<script setup>
import { onMounted, onUnmounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useGlobalTheme } from '@/js/composables/useGlobalTheme.js';
import PseudoNotification from '@/components/common/PseudoNotification.vue';
import { usePseudoNotification } from '@/js/composables/usePseudoNotification';
import { useMyBookings } from '@/js/composables/useMyBookings';
import { useSalonStore } from '@/js/stores/salon';
import { formatBookingWhen } from '@/js/utils/bookingWhen';

const { initializeTheme } = useGlobalTheme();
const salonStore = useSalonStore();
const { show } = usePseudoNotification();
const myBookings = useMyBookings();
const { t, locale } = useI18n();

// A status change observed by the poll becomes the simulated push (#106):
// the realistic in-app banner ported from app-salon-tools, because a real
// push cannot exist here — the vitrina has no service worker on purpose
// (Planning §2.3, parked by René).
// The union polls EVERY salon's bookings (#114), so the banner names the
// BOOKING's salon and formats in ITS timezone via the salons map — a
// confirmation from another salon must never read as sent by this one
// (Codex). The current storefront stays the fallback for its own bookings.
// One branch per transition kind useMyBookings emits (`confirmed` |
// `cancelled`), each passing the translator a LITERAL key. These were template
// literals over `kind`, which the i18n key scan cannot see — and a key the scan
// cannot see is a key whose existence nothing audits, which is how 286 leaves
// went missing unnoticed in the first place (#103). Selecting the translated
// RESULT (not the key) keeps every translator call literal, per the scan's own
// contract. An unknown kind skips the banner instead of rendering a raw key.
function notifyMessages(kind, params) {
  if (kind === 'confirmed') {
    return {
      title: t('customerMenu.notify_confirmed_title'),
      body: t('customerMenu.notify_confirmed_body', params),
    };
  }
  if (kind === 'cancelled') {
    return {
      title: t('customerMenu.notify_cancelled_title'),
      body: t('customerMenu.notify_cancelled_body', params),
    };
  }
  return null;
}

function onTransition({ kind, booking }) {
  const meta = myBookings.salons.value[String(booking.sid ?? '')] || null;
  const when = formatBookingWhen(
    booking.bdt, meta?.tz || salonStore.publicInfo?.tz, locale.value
  );
  const salon = meta?.snm || salonStore.currentSalon?.salonName || 'Salon';
  const message = notifyMessages(kind, { salon, when });
  if (!message) return;
  show(message);
}

// Poll whenever a salon is loaded. Without a contact token each tick is a
// no-op that never touches the network (fetchMyBookings returns before the
// fetch), so this also picks the session up the moment the post-booking mint
// lands — no extra wiring on the wizard.
watch(
  () => salonStore.salonId,
  (salonId) => {
    if (salonId) {
      myBookings.startPolling(salonId, { onTransition });
    } else {
      myBookings.stopPolling();
    }
  },
  { immediate: true }
);

onMounted(() => {
  // Initialize global theme system
  initializeTheme();
});

onUnmounted(() => myBookings.stopPolling());

console.log("Initializing Vitrina Salon");
</script>

<style lang="scss">
// Global styles are imported via main.scss
</style>
